<template>
  <!--
    F2/M3 (balão ①) — segmented control de CATEGORIA acima dos filtros de hoje. Some por
    inteiro quando o backend não serve `counts-by-kind` (`counts === null`): a fila continua
    exatamente como hoje, sem controle sem número e sem número errado.
  -->
  <div
    v-if="counts"
    class="flex flex-wrap items-center gap-2"
    data-testid="ds-attendance-kind-tabs"
  >
    <span class="text-primary30 text-sm font-bold whitespace-nowrap">Categoria:</span>
    <div
      class="inline-flex rounded-full border border-neutral-400 p-0.5"
      role="tablist"
      aria-label="Categoria do registro"
    >
      <button
        v-for="(tab, index) in visibleTabs"
        :key="tab.id"
        type="button"
        role="tab"
        :id="`ds-attendance-kind-tab-${tab.id}`"
        :data-testid="`ds-attendance-kind-tab-${tab.id}`"
        :aria-selected="tab.active"
        :aria-controls="panelId || undefined"
        :tabindex="index === tabbableIndex ? 0 : -1"
        :class="[
          'flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm whitespace-nowrap',
          tab.active
            ? 'is-active bg-primary30 text-white font-bold'
            : 'text-neutral-400 hover:bg-gray-100'
        ]"
        @click="select(tab.value, tab.requests === true)"
        @keydown="onTabKeydown($event, index)"
      >
        {{ tab.label }}
        <b v-if="tab.count !== null" class="text-xs font-bold">{{ tab.count }}</b>
      </button>
    </div>
    <span class="text-xs text-neutral50">A contagem respeita os demais filtros.</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CONTAINER_KIND_LABELS, ContainerKind } from '@/constants/ds-attendance.constants'
import type { ContainerCountsByKind } from '@/Network/Services/DsAttendanceService'

/**
 * Contagem tolerante. O contrato final (B17 §1.1) é `{ total, folha, atividade, requests }`,
 * e é ele que o service tipa; aqui todos os campos seguem OPCIONAIS de propósito — um
 * backend que ainda não sirva algum deles deve deixar a aba sem número, nunca com número
 * errado. `requests` é da F3 e vem `0` na F2.
 */
export type KindTabCounts = Partial<ContainerCountsByKind>

const props = defineProps<{
  /** `null` = "Todos". Array (e não escalar) para acompanhar o `kind` repetível do backend. */
  modelValue?: ContainerKind[] | null
  counts: KindTabCounts | null
  /**
   * F3 — a aba "Solicitações" é um recorte PRÓPRIO (`onlyRequests` do backend), não um
   * `kind`: por isso ela tem o seu próprio `v-model` em vez de caber no `modelValue`.
   * Enquanto a F3 não estiver no ar a prop nem é passada, e nada muda.
   */
  onlyRequests?: boolean
  /**
   * `id` do elemento que estas abas recortam — a listagem da fila. Opcional: sem um id
   * REAL na árvore, `aria-controls` apontaria para o nada, o que é pior para o leitor de
   * tela do que omiti-lo.
   */
  panelId?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: ContainerKind[] | null): void
  (e: 'update:onlyRequests', value: boolean): void
}>()

/** Na aba "Solicitações" nenhuma das outras fica acesa — o recorte é exclusivo. */
const selected = computed<ContainerKind | null>(() =>
  props.onlyRequests ? null : (props.modelValue?.[0] ?? null)
)

const folhaCount = computed(() => props.counts?.folha ?? null)
const atividadeCount = computed(() => props.counts?.atividade ?? null)

/** `total` é o campo do contrato; sem ele, a soma das duas espécies. */
const allCount = computed(() => {
  const counts = props.counts
  if (!counts) return null
  if (typeof counts.total === 'number') return counts.total
  if (typeof counts.folha === 'number' && typeof counts.atividade === 'number') {
    return counts.folha + counts.atividade
  }
  return null
})

/**
 * A aba "Solicitações" NASCE oculta: na F2 o contrato (B10) fixa `requests = 0`, então
 * nenhuma checagem de versão é necessária — quando a F3 começar a devolver número, a aba
 * aparece sozinha.
 *
 * ...mas ela NÃO some enquanto for o recorte ativo (I1). O contador vive aqui e o recorte
 * vive no pai: preencher a última cobrança zerava `requests`, a aba desaparecia com
 * `onlyRequests` ainda ligado e o segmented control ficava sem nenhuma aba acesa — todas
 * com `tabindex="-1"`, fora do alcance do teclado, sobre uma lista recortada sem nada que
 * explicasse o recorte. Com o contador `0` visível o auditor vê onde está e sai por conta.
 */
const hasRequests = computed(() => (props.counts?.requests ?? 0) > 0)
const showRequestsTab = computed(() => hasRequests.value || props.onlyRequests === true)

interface KindTab {
  id: string
  label: string
  value: ContainerKind[] | null
  count: number | null
  active: boolean
  /** F3 — a aba recorta por `onlyRequests`, não por `kind`. */
  requests?: boolean
}

const visibleTabs = computed<KindTab[]>(() => {
  const isRequestsTab = props.onlyRequests === true
  const tabs: KindTab[] = [
    {
      id: 'all',
      label: 'Todos',
      value: null,
      count: allCount.value,
      active: !isRequestsTab && selected.value === null
    },
    {
      id: 'folha',
      label: CONTAINER_KIND_LABELS[ContainerKind.FOLHA],
      value: [ContainerKind.FOLHA],
      count: folhaCount.value,
      active: selected.value === ContainerKind.FOLHA
    },
    {
      id: 'atividade',
      label: CONTAINER_KIND_LABELS[ContainerKind.ATIVIDADE],
      value: [ContainerKind.ATIVIDADE],
      count: atividadeCount.value,
      active: selected.value === ContainerKind.ATIVIDADE
    }
  ]

  // F3 — a aba só nasce quando a política começar a cobrar pernas faltantes. O recorte dela
  // é o atalho `onlyRequests` do backend (§3.1), NÃO o `kind`: por isso `value: null` (a aba
  // não manda `kind`) e a seleção viaja pelo `update:onlyRequests`.
  if (showRequestsTab.value) {
    tabs.push({
      id: 'requests',
      label: 'Solicitações',
      value: null,
      count: props.counts?.requests ?? null,
      active: isRequestsTab,
      requests: true
    })
  }

  return tabs
})

/**
 * Índice da aba tabulável. Sempre existe UMA: sem este fallback um estado em que nenhuma aba
 * está ativa deixaria o `tablist` inteiro com `tabindex="-1"` e inalcançável por teclado.
 */
const tabbableIndex = computed(() => {
  const index = visibleTabs.value.findIndex((tab) => tab.active)
  return index === -1 ? 0 : index
})

/**
 * Os dois recortes são mutuamente exclusivos, e os dois eventos saem SEMPRE juntos: trocar de
 * aba sem desligar o `onlyRequests` deixaria a fila mostrando solicitações sob o rótulo
 * "Folha", e o pai não tem como adivinhar que precisa zerar o outro lado.
 */
function select(value: ContainerKind[] | null, requests = false) {
  emit('update:onlyRequests', requests)
  emit('update:modelValue', requests ? null : value)
}

/**
 * Navegação de `tablist` (I17): num grupo de abas o Tab entra UMA vez e as setas trocam de
 * aba — por isso o `tabindex` roving acima. A ativação é automática (a seta já seleciona),
 * como no resto dos filtros desta fila, onde a escolha é imediata e reversível.
 *
 * `aria-controls` aponta para a listagem: a tela de Aprovações passou a dar um `id` estável
 * ao container da lista e a marcá-lo como `tabpanel`. Sem o `panelId` (uso fora daquela
 * tela) o atributo simplesmente não é emitido — apontar para um id inexistente é pior para
 * o leitor de tela do que não apontar.
 */
function onTabKeydown(event: KeyboardEvent, index: number) {
  const tabs = visibleTabs.value
  if (!tabs.length) return

  let nextIndex: number | null = null
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    nextIndex = (index + 1) % tabs.length
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    nextIndex = (index - 1 + tabs.length) % tabs.length
  } else if (event.key === 'Home') {
    nextIndex = 0
  } else if (event.key === 'End') {
    nextIndex = tabs.length - 1
  }
  if (nextIndex === null) return

  event.preventDefault()
  const target = event.currentTarget as HTMLElement | null
  const sibling = target?.parentElement?.children.item(nextIndex)
  if (sibling instanceof HTMLElement) sibling.focus()
  select(tabs[nextIndex].value, tabs[nextIndex].requests === true)
}
</script>
